import { useEffect, useMemo, useState } from 'react';
import {
  addExpense,
  deleteExpense,
  getFinance,
  updateExpense,
  type Expense,
  type ExpenseType,
  type FinanceData,
} from '../../api';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useAuth } from '../../hooks/useAuth';
import { useFeedback } from '../../hooks/useFeedback';
import { useLoadMore } from '../../hooks/useLoadMore';
import { formatMoney } from '../../utils/formatMoney';
import { DateFormat } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import { downloadExpensesCsv } from '../../utils/exportExpensesCsv';
import {
  FINANCE_LAST_PERIOD,
  FINANCE_PERIOD_SHORT_MAX_WIDTH,
  financePeriodOptionText,
  financePeriodOptions,
} from '../../utils/financePeriod';
import {
  EXPENSE_CATEGORY_ROWS,
  EXPENSE_TYPE_OPTIONS,
  expenseItemLabel,
  expenseRowDetail,
  expenseSplitLabel,
  expenseTypeLabel,
} from '../../utils/expenseTypes';
import {
  SortConfig,
  SortValueKind,
  defaultDirectionForKind,
  sortByConfig,
  toggleSortConfig,
} from '../../utils/tableSort';
import PageBackLink from '../../components/PageBackLink/PageBackLink';
import AddExpenseModal from '../../components/AddExpenseModal/AddExpenseModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import RecordDetailsModal from '../../components/RecordDetailsModal/RecordDetailsModal';
import LoadMoreButton from '../../components/LoadMoreButton/LoadMoreButton';
import SortableTh from '../../components/SortableTh/SortableTh';
import '../AircraftPage/AircraftPage.css';
import '../IssuesPage/IssuesPage.css';
import '../FlightsHistoryPage/FlightsHistoryPage.css';
import './FinancePage.css';

type ExpenseSortKey = keyof Expense;

type PeriodSelectProps = {
  value: string;
  years: Array<{ year: number }>;
  onChange: (value: string) => void;
  ariaLabel: string;
  capitalize?: boolean;
  shortOnNarrow?: boolean;
};

const useShortPeriodLabels = () => {
  const [short, setShort] = useState(
    () => window.matchMedia(`(max-width: ${FINANCE_PERIOD_SHORT_MAX_WIDTH}px)`).matches
  );

  useEffect(() => {
    const media = window.matchMedia(
      `(max-width: ${FINANCE_PERIOD_SHORT_MAX_WIDTH}px)`
    );
    const sync = () => setShort(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return short;
};

const FinancePeriodSelect = ({
  value,
  years,
  onChange,
  ariaLabel,
  capitalize = false,
  shortOnNarrow = false,
}: PeriodSelectProps) => {
  const short = useShortPeriodLabels() && shortOnNarrow;

  return (
    <select
      className="finance-period-select"
      value={value}
      aria-label={ariaLabel}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => onChange(event.target.value)}
    >
      {financePeriodOptions(years, capitalize).map((option) => (
        <option key={option.value} value={option.value}>
          {financePeriodOptionText(option, short)}
        </option>
      ))}
    </select>
  );
};

const isManualExpenseId = (id: Expense['id']) =>
  String(id).startsWith('exp-');

const emptyTotals = (): FinanceData['categories']['all'] => {
  const totals = Object.fromEntries(
    EXPENSE_CATEGORY_ROWS.map((row) => [row.key, 0])
  ) as Record<ExpenseType, number>;
  return { ...totals, total: 0 };
};

const emptyFinance = (): FinanceData => ({
  year_start: '',
  purchase_date: null,
  hours_all: 0,
  hours_year: 0,
  hour_cost_all: null,
  hour_cost_year: null,
  categories: {
    all: emptyTotals(),
    year: emptyTotals(),
  },
  years: [],
  items: [],
});

const sortKind = (key: ExpenseSortKey): SortValueKind => {
  switch (key) {
    case 'expense_date':
      return 'date';
    case 'amount':
      return 'number';
    default:
      return 'string';
  }
};

const FinancePage = () => {
  const { canMaintain } = useAuth();
  const { aircraftId, dataRevision } = useAircraft();
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const [data, setData] = useState<FinanceData>(emptyFinance());
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState<Expense | null>(null);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [deleting, setDeleting] = useState<Expense | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [typeFilter, setTypeFilter] = useState<ExpenseType | ''>('');
  const [hourPeriod, setHourPeriod] = useState(FINANCE_LAST_PERIOD);
  const [tablePeriod, setTablePeriod] = useState(FINANCE_LAST_PERIOD);
  const [sortConfig, setSortConfig] = useState<SortConfig<ExpenseSortKey>>({
    key: 'expense_date',
    direction: 'desc',
  });

  const loadData = async () => {
    try {
      const res = await getFinance();
      setData(res.data);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка загрузки финансов'));
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      try {
        const res = await getFinance({ signal: controller.signal });
        setData(res.data);
      } catch (error) {
        if (controller.signal.aborted) return;
        showError(getApiErrorMessage(error, 'Ошибка загрузки финансов'));
      }
    };
    void run();
    return () => controller.abort();
  }, [aircraftId, dataRevision, showError]);

  useEffect(() => {
    setHourPeriod(FINANCE_LAST_PERIOD);
    setTablePeriod(FINANCE_LAST_PERIOD);
  }, [aircraftId]);

  const yearBuckets = useMemo(() => data.years || [], [data.years]);
  useEffect(() => {
    const hasYear = (period: string) =>
      period === FINANCE_LAST_PERIOD ||
      yearBuckets.some((row) => String(row.year) === period);
    if (!hasYear(hourPeriod)) setHourPeriod(FINANCE_LAST_PERIOD);
    if (!hasYear(tablePeriod)) setTablePeriod(FINANCE_LAST_PERIOD);
  }, [hourPeriod, tablePeriod, yearBuckets]);

  const selectedHourYear = useMemo(
    () => yearBuckets.find((row) => String(row.year) === hourPeriod) || null,
    [hourPeriod, yearBuckets]
  );
  const selectedTableYear = useMemo(
    () => yearBuckets.find((row) => String(row.year) === tablePeriod) || null,
    [tablePeriod, yearBuckets]
  );
  const periodHourCost =
    hourPeriod === FINANCE_LAST_PERIOD
      ? data.hour_cost_year
      : selectedHourYear?.hour_cost ?? null;
  const periodCategories =
    tablePeriod === FINANCE_LAST_PERIOD
      ? data.categories.year
      : selectedTableYear?.categories ?? emptyTotals();

  const handleSort = (key: ExpenseSortKey) => {
    setSortConfig((prev) =>
      toggleSortConfig(prev, key, defaultDirectionForKind(sortKind(key)))
    );
  };

  const sortedItems = useMemo(() => {
    const filtered = typeFilter
      ? data.items.filter((item) => item.expense_type === typeFilter)
      : data.items;
    return sortByConfig(filtered, sortConfig, (row, key) => row[key], sortKind);
  }, [data.items, sortConfig, typeFilter]);

  const { visibleCount, hasMore, remaining, nextBatch, loadMore, loadAll } = useLoadMore(
    sortedItems.length,
    {
      resetKey: `${sortConfig.key}-${sortConfig.direction}-${typeFilter}`,
    }
  );
  const visibleItems = sortedItems.slice(0, visibleCount);

  const handleDeleteConfirm = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await deleteExpense(deleting.id);
      setDeleting(null);
      await loadData();
      showSuccess('Расход удалён');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка удаления расхода'));
    }
    setDeleteBusy(false);
  };

  return (
    <div className="aircraft-page finance-page">
      {feedbackNode}
      <div className="aircraft-hub">
        <div className="aircraft-header">
          <PageBackLink to="/aircraft">← Самолёт</PageBackLink>
          <h2>Финансы</h2>
        </div>

        <div className="aircraft-hub-grid finance-metrics">
          <div className="aircraft-hub-card is-static">
            <span className="aircraft-hub-label">Стоимость лётного часа</span>
            <span className="aircraft-hub-value">
              <span className="aircraft-hub-number">
                {formatMoney(data.hour_cost_all)}
                {data.hour_cost_all != null ? (
                  <span className="aircraft-hub-unit">₽</span>
                ) : null}
              </span>
            </span>
            <span className="aircraft-hub-footer">за всё время</span>
          </div>
          <div className="aircraft-hub-card is-static">
            <span className="aircraft-hub-label">Стоимость лётного часа</span>
            <span className="aircraft-hub-value">
              <span className="aircraft-hub-number">
                {formatMoney(periodHourCost)}
                {periodHourCost != null ? (
                  <span className="aircraft-hub-unit">₽</span>
                ) : null}
              </span>
            </span>
            <span className="aircraft-hub-footer">
              <FinancePeriodSelect
                value={hourPeriod}
                years={yearBuckets}
                onChange={setHourPeriod}
                ariaLabel="Период стоимости лётного часа"
              />
            </span>
          </div>
        </div>

        {canMaintain ? (
          <button
            type="button"
            className="page-cta-btn"
            onClick={() => setAdding(true)}
          >
            Добавить расход
          </button>
        ) : null}

        <div className="finance-panel">
          <h3>Расходы всего / за год</h3>
          <table className="finance-totals">
            <thead>
              <tr>
                <th></th>
                <th>Всего</th>
                <th>
                  <FinancePeriodSelect
                    value={tablePeriod}
                    years={yearBuckets}
                    onChange={setTablePeriod}
                    capitalize
                    shortOnNarrow
                    ariaLabel="Период расходов"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {EXPENSE_CATEGORY_ROWS.map((row) => (
                <tr key={row.key}>
                  <td>{row.label}</td>
                  <td>{formatMoney(data.categories.all[row.key])} ₽</td>
                  <td>{formatMoney(periodCategories[row.key])} ₽</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="finance-panel finance-history">
          <div className="finance-history-head">
            <h3>История расходов</h3>
            <button
              type="button"
              className="btn-export-flights"
              onClick={() => downloadExpensesCsv(sortedItems)}
              disabled={sortedItems.length === 0}
            >
              Скачать CSV
            </button>
            <label className="finance-type-filter">
              Тип
              <select
                value={typeFilter}
                onChange={(e) =>
                  setTypeFilter((e.target.value || '') as ExpenseType | '')
                }
                aria-label="Фильтр по типу расхода"
              >
                <option value="">Все</option>
                {EXPENSE_TYPE_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flights-history-table-wrap">
            <table className="flights-history-table flights-history-table-compact">
              <thead>
                <tr>
                  <SortableTh
                    label="Дата"
                    columnKey="expense_date"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Тип"
                    columnKey="expense_type"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Сумма"
                    columnKey="amount"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {visibleItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d' }}
                    >
                      Нет расходов{typeFilter ? ' этого типа' : ''}
                    </td>
                  </tr>
                ) : (
                  visibleItems.map((item) => {
                    const typeLabel = expenseTypeLabel(item.expense_type);
                    const detail = expenseRowDetail(
                      item.expense_type,
                      item.title,
                      item.comment
                    );
                    const rowLabel = expenseItemLabel(
                      item.expense_type,
                      item.title,
                      item.comment
                    );
                    return (
                      <tr
                        key={item.id}
                        className="flights-history-row"
                        onClick={() => setViewing(item)}
                      >
                        <td>{DateFormat(item.expense_date)}</td>
                        <td className="record-row-main" title={rowLabel}>
                          {typeLabel}
                          {detail ? (
                            <span className="expense-row-detail">
                              , {detail}
                            </span>
                          ) : null}
                        </td>
                        <td>{formatMoney(item.amount)} ₽</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {hasMore ? (
            <LoadMoreButton
              remaining={remaining}
              nextBatch={nextBatch}
              onClick={loadMore}
              onShowAll={loadAll}
            />
          ) : null}
        </div>
      </div>

      {adding ? (
        <AddExpenseModal
          onClose={() => setAdding(false)}
          onConfirm={async (payload) => {
            try {
              await addExpense(payload);
              setAdding(false);
              await loadData();
              showSuccess('Расход добавлен');
            } catch (error) {
              showError(getApiErrorMessage(error, 'Ошибка добавления расхода'));
            }
          }}
        />
      ) : null}

      {viewing ? (
        <RecordDetailsModal
          title="Расход"
          summary={
            <>
              <span className="record-details-date">
                {DateFormat(viewing.expense_date)}
              </span>
              <span className="record-details-lead">
                {expenseTypeLabel(viewing.expense_type)}
              </span>
            </>
          }
          rows={[
            ...(viewing.expense_type === 'other' && viewing.title?.trim()
              ? [{ label: 'Название', value: viewing.title.trim() }]
              : []),
            { label: 'Сумма', value: `${formatMoney(viewing.amount)} ₽` },
            {
              label: 'Распределение',
              value:
                expenseSplitLabel(viewing.cost_split, viewing.expense_type) ||
                '—',
            },
          ]}
          note={viewing.comment}
          noteLabel="Комментарий"
          actions={
            canMaintain && isManualExpenseId(viewing.id)
              ? [
                  {
                    label: 'Изменить',
                    onClick: () => {
                      setEditing(viewing);
                      setViewing(null);
                    },
                  },
                  {
                    label: 'Удалить',
                    onClick: () => {
                      setDeleting(viewing);
                      setViewing(null);
                    },
                    danger: true,
                  },
                ]
              : []
          }
          onClose={() => setViewing(null)}
        />
      ) : null}

      {editing ? (
        <AddExpenseModal
          title="Изменить расход"
          confirmLabel="Сохранить"
          initial={{
            expense_date: String(editing.expense_date).slice(0, 10),
            expense_type: editing.expense_type,
            amount: editing.amount,
            cost_split: editing.cost_split || 'shares',
            payer_ids: editing.payer_ids || [],
            payer_shares: editing.payer_shares || [],
            flight_ids: editing.flight_ids || [],
            title: editing.title || '',
            comment: editing.comment || '',
          }}
          onClose={() => setEditing(null)}
          onConfirm={async (payload) => {
            try {
              await updateExpense(editing.id, payload);
              setEditing(null);
              await loadData();
              showSuccess('Расход обновлён');
            } catch (error) {
              showError(getApiErrorMessage(error, 'Ошибка изменения расхода'));
            }
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmModal
          title="Удалить расход?"
          message={`${expenseTypeLabel(deleting.expense_type)} на ${formatMoney(deleting.amount)} ₽ от ${DateFormat(deleting.expense_date)}. Сумма также исчезнет из личных финансов участников.`}
          confirmLabel="Удалить"
          danger
          submitting={deleteBusy}
          onCancel={() => {
            if (!deleteBusy) setDeleting(null);
          }}
          onConfirm={() => void handleDeleteConfirm()}
        />
      ) : null}
    </div>
  );
};

export default FinancePage;
